import { useRef, useState } from "react";
import { motion, useSpring, useTransform } from "motion/react";
import { Link, useLocation } from "@tanstack/react-router";
import { ProductSigilIcon } from "./ProductSigilIcon";
import { ProductSigil } from "../data/productSigils";

interface DockIconProps {
  mouseX: any;
  product: ProductSigil;
}

export function DockIcon({ mouseX, product }: DockIconProps) {
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  
  const isProductRoute = location.pathname.startsWith(`/product/${product.slug}`);
  const isHomeEXP = location.pathname === '/' && product.slug === 'exp';
  const isActive = isProductRoute || isHomeEXP;

  const distance = useTransform(mouseX, (val: number) => {
    const bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };
    return val - bounds.x - bounds.width / 2;
  });

  const widthSync = useTransform(distance, [-150, 0, 150], [56, 96, 56]);
  const width = useSpring(widthSync, { mass: 0.1, stiffness: 150, damping: 12 });

  const [hovered, setHovered] = useState(false);

  return (
    <div className="relative group/dock flex flex-col items-center">
      <motion.div
        ref={ref}
        style={{ width, height: width }}
        className="flex items-center justify-center relative cursor-pointer group-hover:z-10"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Link 
          to={(product.slug === 'exp' ? '/' : `/product/${product.slug}`) as string}
          className="w-full h-full flex items-center justify-center outline-none ring-0 relative transition-transform duration-200 active:scale-95"
        >
          <div className="absolute inset-0 bg-white/5 rounded-2xl border border-white/5 shadow-sm group-hover/dock:border-white/10 group-hover/dock:bg-white/10 transition-all duration-300 pointer-events-none" />
          <motion.div style={{ width: "75%", height: "75%" }} className="relative z-10 flex items-center justify-center">
            <ProductSigilIcon 
              slug={product.slug} 
              className="w-full h-full object-contain drop-shadow-[0_0_8px_#00000080]" 
              isActive={isActive || hovered}
              accentColor={product.accentColor}
            />
          </motion.div>
        </Link>
      </motion.div>
      
      <div 
        className="absolute -bottom-10 opacity-0 group-hover/dock:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap bg-[var(--p-gray-900)] border border-white/10 px-3 py-1.5 rounded-lg text-sm font-medium text-white z-50 shadow-xl flex items-center gap-2"
      >
        <div className="w-2 h-2 rounded-full shadow-[0_0_8px_currentColor]" style={{ backgroundColor: product.accentColor, color: product.accentColor }} />
        {product.name}
      </div>
      
      <div className={`absolute -bottom-3 w-1.5 h-1.5 rounded-full shadow-[0_0_8px_currentColor] transition-all duration-300 ${isActive ? 'opacity-100' : 'opacity-0'}`} style={{ backgroundColor: product.accentColor, color: product.accentColor }} />
    </div>
  );
}
